/**
 * BetInspector — Phase 2.D
 *
 * Searchable, filterable, sortable table of every bet across every
 * sport. Row click opens a side drawer with the full bet dossier
 * (LLM reasoning, ai_picker_thesis, hr_bet_id, closing line, CLV).
 *
 * No virtualization yet — total bet count plateaus in the few-hundreds
 * for active research; CSS overflow handles the scroll cleanly. If we
 * ever cross ~5k rows we drop in @tanstack/virtual.
 */
import { html, type TemplateResult } from "lit-html";
import { store } from "../data/store";
import type { BetsResponse, Bet } from "../data/types";
import { renderExplainButton } from "./ExplainButton";

type SportFilter   = "ALL" | "NBA" | "WNBA" | "MLB" | "NHL" | "NFL" | "SOCCER" | "UFC";
type OutcomeFilter = "ALL" | "win" | "loss" | "pending" | "void";
type SortKey       = "placed_at" | "llm_confidence" | "edge_pp" | "pnl";

interface ViewState {
  search:        string;
  sportFilter:   SportFilter;
  outcomeFilter: OutcomeFilter;
  sortKey:       SortKey;
  sortDir:       "asc" | "desc";
  selectedId:    string | null;
}

const view: ViewState = {
  search:        "",
  sportFilter:   "ALL",
  outcomeFilter: "ALL",
  sortKey:       "placed_at",
  sortDir:       "desc",
  selectedId:    null,
};

/** Imperative deep-link entry — Cmd-K's query_bets affordance writes
 *  filters here before switching the active tab. Unspecified fields
 *  reset to "ALL"/"" so a fresh deep-link doesn't inherit previous state. */
export function setBetInspectorFilters(f: {
  sport?:   string;
  outcome?: string;
  search?:  string;
}): void {
  const sportUp = (f.sport ?? "").toUpperCase();
  const sport: SportFilter =
    sportUp === "NBA" || sportUp === "WNBA" || sportUp === "MLB" || sportUp === "NHL"
      || sportUp === "NFL" || sportUp === "SOCCER" || sportUp === "UFC" ? sportUp : "ALL";
  const out = f.outcome ?? "";
  const outcome: OutcomeFilter =
    out === "win" || out === "loss" || out === "pending" || out === "void"
      ? out : "ALL";
  view.search        = f.search ?? "";
  view.sportFilter   = sport;
  view.outcomeFilter = outcome;
  view.selectedId    = null;
}

export function renderBetInspector(onChange: () => void): TemplateResult {
  const e = store.get<BetsResponse>("bets");

  if (e.error) return errorView(e.error);
  if (!e.data && e.loading) return skeletonView();
  if (!e.data) return emptyView();

  const all = e.data.bets;
  const filtered = applyFilters(all);
  const selected = view.selectedId
    ? all.find((b) => b.id === view.selectedId) ?? null
    : null;

  return html`
    <div class="bet-inspector">
      ${headerCard(all, filtered, onChange)}

      <section class="panel bet-inspector__table-panel">
        <div class="bet-inspector__table-scroll">
          <table class="bet-inspector__table">
            <thead>
              <tr>
                ${headerCell("placed_at",      "Time",  onChange)}
                <th>Sport</th>
                <th>Player / Matchup</th>
                <th>Market</th>
                <th>Side</th>
                <th>Line</th>
                <th>Price</th>
                ${headerCell("llm_confidence", "LLM",   onChange)}
                ${headerCell("edge_pp",        "Edge",  onChange)}
                ${headerCell("pnl",            "PNL",   onChange)}
                <th>Outcome</th>
              </tr>
            </thead>
            <tbody>
              ${filtered.length === 0 ? html`
                <tr><td colspan="11" class="bet-inspector__empty">
                  No bets match the current filters.
                </td></tr>
              ` : filtered.map((b) => row(b, () => {
                view.selectedId = b.id;
                onChange();
              }))}
            </tbody>
          </table>
        </div>
      </section>

      ${selected ? betDossier(selected, () => {
        view.selectedId = null;
        onChange();
      }) : ""}
    </div>
  `;
}

// =============================================================
// Header card — search + filters + count
// =============================================================
function headerCard(all: Bet[], filtered: Bet[], onChange: () => void): TemplateResult {
  const sports: SportFilter[]     = ["ALL", "NBA", "WNBA", "MLB", "NHL", "NFL", "SOCCER", "UFC"];
  const outcomes: OutcomeFilter[] = ["ALL", "win", "loss", "pending", "void"];

  return html`
    <section class="panel bet-inspector__head">
      <div class="bet-inspector__head-row">
        <input
          class="bet-inspector__search"
          type="search"
          placeholder="Search player, matchup, market, strategy_id…"
          .value=${view.search}
          @input=${(e: Event) => {
            view.search = (e.target as HTMLInputElement).value;
            onChange();
          }}
        />

        <div class="bet-inspector__filter-group">
          ${sports.map((s) => html`
            <button
              class="bet-inspector__chip ${view.sportFilter === s ? "is-active" : ""}"
              @click=${() => { view.sportFilter = s; onChange(); }}
            >${s}</button>
          `)}
        </div>

        <div class="bet-inspector__filter-group bet-inspector__filter-group--outcomes">
          ${outcomes.map((o) => html`
            <button
              class="bet-inspector__chip is-outcome-${o}
                     ${view.outcomeFilter === o ? "is-active" : ""}"
              @click=${() => { view.outcomeFilter = o; onChange(); }}
            >${o}</button>
          `)}
        </div>

        <span class="bet-inspector__spacer"></span>

        <span class="bet-inspector__count">
          ${filtered.length} / ${all.length} bets
        </span>

        ${renderExplainButton({
          label: "Explain bet flow",
          context: () => buildSummaryExplainContext(all, filtered),
        })}
      </div>
    </section>
  `;
}

function headerCell(key: SortKey, label: string, onChange: () => void): TemplateResult {
  const active = view.sortKey === key;
  const arrow = active ? (view.sortDir === "asc" ? "↑" : "↓") : "";
  return html`
    <th class="bet-inspector__sortable-th ${active ? "is-active" : ""}"
        @click=${() => {
          if (view.sortKey === key) {
            view.sortDir = view.sortDir === "asc" ? "desc" : "asc";
          } else {
            view.sortKey = key;
            view.sortDir = "desc";
          }
          onChange();
        }}>
      <span class="bet-inspector__sortable">
        <span>${label}</span>
        <span class="bet-inspector__sort-arrow">${arrow}</span>
      </span>
    </th>
  `;
}

// =============================================================
// Row
// =============================================================
function row(b: Bet, onClick: () => void): TemplateResult {
  const time = b.placed_at ? new Date(b.placed_at).toLocaleString([], {
    month: "short", day: "numeric",
    hour: "2-digit", minute: "2-digit",
  }) : "—";

  const sportColor: Record<string, string> = {
    NBA:    "var(--accent-orange-light)",
    WNBA:   "#2dd4bf",
    MLB:    "var(--accent-blue-light)",
    NHL:    "var(--accent-purple-light)",
    SOCCER: "var(--accent-green-light)",
    UFC:    "var(--accent-red-light)",
  };
  const sport = (b.sport || "—").toUpperCase();

  const matchup = b.player ?? b.matchup ?? `${b.team ?? ""} vs ${b.opp ?? ""}`;
  const outcome = b.outcome || "pending";
  const outcomeCls = `is-${outcome}`;

  const llmConf = b.llm_confidence;
  const llmCls =
    llmConf == null ? "is-idle"
    : llmConf >= 70 ? "is-ok"
    : llmConf >= 50 ? "is-warn"
    : "is-bad";

  const edgePct = b.edge_pp;
  const edgeCls =
    edgePct == null ? ""
    : edgePct > 0 ? "is-pos"
    : "is-neg";

  const pnl = b.pnl;
  const pnlCls = pnl == null ? "" : pnl > 0 ? "is-pos" : pnl < 0 ? "is-neg" : "";

  return html`
    <tr class="bet-inspector__row ${view.selectedId === b.id ? "is-selected" : ""}"
        @click=${onClick}>
      <td class="bet-inspector__time">${time}</td>
      <td><span class="bet-inspector__sport-pill"
                 style="color:${sportColor[sport] ?? "var(--text-secondary)"};">
        ${sport}
      </span></td>
      <td class="bet-inspector__matchup">${matchup}</td>
      <td class="bet-inspector__market">${b.market ?? "—"}</td>
      <td>${b.side ?? "—"}</td>
      <td class="bet-inspector__num">${b.line ?? "—"}</td>
      <td class="bet-inspector__num">${b.price ?? "—"}</td>
      <td class="bet-inspector__num">
        <span class="bet-inspector__llm ${llmCls}">${llmConf ?? "—"}</span>
      </td>
      <td class="bet-inspector__num ${edgeCls}">
        ${edgePct == null ? "—" : `${edgePct > 0 ? "+" : ""}${edgePct.toFixed(1)}pp`}
      </td>
      <td class="bet-inspector__num ${pnlCls}">
        ${pnl == null ? "—" : `${pnl > 0 ? "+" : ""}${pnl.toFixed(2)}u`}
      </td>
      <td>
        <span class="bet-inspector__outcome ${outcomeCls}">${outcome}</span>
      </td>
    </tr>
  `;
}

// =============================================================
// Bet dossier drawer
// =============================================================
function betDossier(b: Bet, onClose: () => void): TemplateResult {
  return html`
    <aside class="bet-dossier glass">
      <header class="bet-dossier__head">
        <span class="bet-dossier__sport">${(b.sport || "?").toUpperCase()}</span>
        <h3>${b.player ?? b.matchup ?? "—"}</h3>
        <button class="bet-dossier__close" @click=${onClose}>×</button>
      </header>

      <div class="bet-dossier__body">
        ${dossierSection("Pick", html`
          <div class="bet-dossier__pill-row">
            ${pillKv("market",  b.market)}
            ${pillKv("side",    b.side)}
            ${pillKv("line",    b.line)}
            ${pillKv("price",   b.price)}
            ${pillKv("stake",   b.stake)}
            ${pillKv("book",    b.book)}
            ${pillKv("strategy", b.strategy_id)}
          </div>
        `)}

        ${dossierSection("LLM", html`
          <div class="bet-dossier__pill-row">
            ${pillKv("conf",  b.llm_confidence)}
            ${pillKv("tier",  b.llm_tier)}
            ${pillKv("flags", (b.llm_flags ?? []).join(", ") || null)}
            ${pillKv("scored", fmtTime(b.llm_scored_at))}
          </div>
          ${b.llm_reasoning ? html`
            <div class="bet-dossier__prose">${b.llm_reasoning}</div>
          ` : ""}
        `)}

        ${b.ai_picker_thesis || b.ai_picker_convictions ? dossierSection("Picker thesis", html`
          ${b.ai_picker_thesis ? html`
            <div class="bet-dossier__prose">${b.ai_picker_thesis}</div>
          ` : ""}
          ${b.ai_picker_convictions ? html`
            <div class="bet-dossier__pill-row" style="margin-top:8px;">
              ${Object.entries(b.ai_picker_convictions)
                .filter(([, v]) => v != null)
                .map(([k, v]) => pillKv(k, v as number))}
            </div>
          ` : ""}
        `) : ""}

        ${dossierSection("Edge / market", html`
          <div class="bet-dossier__pill-row">
            ${pillKv("our_prob_pct",   b.our_prob_pct)}
            ${pillKv("implied_prob_pct", b.implied_prob_pct)}
            ${pillKv("edge_pp",        b.edge_pp)}
            ${pillKv("ev_per_unit",    b.ev_per_unit)}
            ${pillKv("expected_close", b.expected_close)}
            ${pillKv("closing_line",   b.closing_line)}
            ${pillKv("closing_price",  b.closing_price)}
            ${pillKv("clv",            b.clv)}
          </div>
        `)}

        ${dossierSection("Result", html`
          <div class="bet-dossier__pill-row">
            ${pillKv("outcome",     b.outcome)}
            ${pillKv("pnl",         b.pnl)}
            ${pillKv("result_stat", b.result_stat)}
            ${pillKv("graded_at",   fmtTime(b.graded_at))}
          </div>
        `)}

        ${dossierSection("HR resolution", html`
          <div class="bet-dossier__pill-row">
            ${pillKv("hr_bet_id", b.hr_bet_id)}
          </div>
          ${b.injury_context ? html`
            <div class="bet-dossier__prose" style="font-size:11px;">
              <em>${b.injury_context}</em>
            </div>
          ` : ""}
        `)}

        ${b.notes ? dossierSection("Notes", html`
          <div class="bet-dossier__prose">${b.notes}</div>
        `) : ""}

        <div class="bet-dossier__actions">
          ${renderExplainButton({
            label: "Explain this bet",
            context: () => buildBetExplainContext(b),
          })}
          <span class="bet-dossier__id">id: ${b.id}</span>
        </div>
      </div>
    </aside>
  `;
}

function dossierSection(title: string, body: TemplateResult): TemplateResult {
  return html`
    <section class="bet-dossier__section">
      <h4>${title}</h4>
      ${body}
    </section>
  `;
}

function pillKv(k: string, v: unknown): TemplateResult | "" {
  if (v == null || v === "") return "";
  const display = typeof v === "number" ? formatNumber(v) : String(v);
  return html`
    <span class="bet-dossier__pill">
      <span class="bet-dossier__pill-k">${k}</span>
      <span class="bet-dossier__pill-v">${display}</span>
    </span>
  `;
}

// =============================================================
// Filtering / sorting
// =============================================================
function applyFilters(bets: Bet[]): Bet[] {
  const q = view.search.trim().toLowerCase();
  let out = bets;

  if (view.sportFilter !== "ALL") {
    out = out.filter((b) => (b.sport || "").toUpperCase() === view.sportFilter);
  }
  if (view.outcomeFilter !== "ALL") {
    out = out.filter((b) => (b.outcome || "pending") === view.outcomeFilter);
  }
  if (q) {
    out = out.filter((b) => {
      const hay = [
        b.player, b.matchup, b.team, b.opp,
        b.market, b.strategy_id, b.notes,
        b.ai_picker_thesis, b.llm_reasoning,
      ].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(q);
    });
  }

  // Sort
  const dir = view.sortDir === "asc" ? 1 : -1;
  out = [...out].sort((a, b) => {
    const av = (a as any)[view.sortKey];
    const bv = (b as any)[view.sortKey];
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    if (typeof av === "number" && typeof bv === "number") return (av - bv) * dir;
    return String(av).localeCompare(String(bv)) * dir;
  });
  return out;
}

// =============================================================
// Helpers
// =============================================================
function formatNumber(n: number): string {
  if (Math.abs(n) >= 1000) return n.toLocaleString();
  if (!Number.isInteger(n)) return n.toFixed(2);
  return String(n);
}
function fmtTime(s: string | undefined): string | null {
  if (!s) return null;
  return new Date(s).toLocaleString();
}

function buildSummaryExplainContext(all: Bet[], filtered: Bet[]): string {
  const decided = all.filter((b) => b.outcome === "win" || b.outcome === "loss");
  const wins = decided.filter((b) => b.outcome === "win").length;
  const totalPnl = decided.reduce((s, b) => s + (b.pnl ?? 0), 0);
  const bySport: Record<string, { n: number; w: number; l: number; pnl: number }> = {};
  for (const b of all) {
    const k = (b.sport || "?").toUpperCase();
    bySport[k] = bySport[k] ?? { n: 0, w: 0, l: 0, pnl: 0 };
    bySport[k].n++;
    if (b.outcome === "win")  bySport[k].w++;
    if (b.outcome === "loss") bySport[k].l++;
    bySport[k].pnl += b.pnl ?? 0;
  }
  const lines = [
    `Bet inspector summary:`,
    `${all.length} total bets · ${decided.length} decided · ${wins} wins · P&L ${totalPnl.toFixed(2)}u`,
    `${filtered.length} match current filters (sport=${view.sportFilter}, outcome=${view.outcomeFilter}, search="${view.search}")`,
    "",
    "Per-sport:",
    ...Object.entries(bySport).map(([k, v]) =>
      `  ${k}: ${v.n}n · ${v.w}W ${v.l}L · ${v.pnl.toFixed(2)}u`,
    ),
  ];
  return lines.join("\n");
}

function buildBetExplainContext(b: Bet): string {
  return [
    `Bet ${b.id} (${b.sport}):`,
    `  Pick: ${b.market} ${b.side ?? ""} ${b.line ?? ""} @ ${b.price ?? "?"}, stake ${b.stake ?? "?"}u`,
    `  Player/matchup: ${b.player ?? b.matchup ?? "?"}`,
    `  Strategy: ${b.strategy_id ?? "?"}`,
    `  LLM: confidence=${b.llm_confidence ?? "?"} tier=${b.llm_tier ?? "?"} flags=${(b.llm_flags ?? []).join(",")}`,
    `  Edge: our=${b.our_prob_pct ?? "?"}% implied=${b.implied_prob_pct ?? "?"}% edge=${b.edge_pp ?? "?"}pp ev=${b.ev_per_unit ?? "?"}`,
    `  Closing: line=${b.closing_line ?? "?"} price=${b.closing_price ?? "?"} clv=${b.clv ?? "?"}`,
    `  Outcome: ${b.outcome ?? "pending"}, pnl=${b.pnl ?? "?"}, graded_at=${b.graded_at ?? "?"}`,
    `  HR bet_id: ${b.hr_bet_id ?? "?"}`,
    "",
    "LLM reasoning:",
    b.llm_reasoning ?? "(none)",
    "",
    "Picker thesis:",
    b.ai_picker_thesis ?? "(none)",
  ].join("\n");
}

// =============================================================
// State views
// =============================================================
function skeletonView(): TemplateResult {
  return html`
    <div class="bet-inspector">
      <section class="panel" style="height:60px;display:flex;align-items:center;padding:0 24px;">
        <div class="skeleton" style="width:30%;height:14px;"></div>
      </section>
      <section class="panel" style="min-height:480px;padding:24px;">
        ${Array.from({ length: 8 }, () => html`
          <div class="skeleton" style="height:18px;margin-bottom:8px;"></div>
        `)}
      </section>
    </div>
  `;
}

function emptyView(): TemplateResult {
  return html`
    <div class="panel" style="padding:48px;text-align:center;">
      <div style="color:var(--text-tertiary);font-size:14px;line-height:1.5;
                   max-width:480px;margin:0 auto;">
        No bets yet. Source: dashboard.json's per-sport bets blocks.
      </div>
    </div>
  `;
}

function errorView(err: Error): TemplateResult {
  return html`
    <div class="panel" style="padding:32px;border-color:rgba(255,69,58,0.32);">
      <div class="panel__title">
        <span>Bet Inspector</span>
        <span class="phase-stub-badge"
              style="background:rgba(255,69,58,0.16);
                      color:var(--accent-red-light);
                      border-color:rgba(255,69,58,0.32);">
          fetch error
        </span>
      </div>
      <div style="color:var(--accent-red-light);font-size:12px;
                   line-height:1.5;font-family:monospace;
                   word-break:break-all;white-space:pre-wrap;
                   margin-top:8px;">
        ${err.message}
      </div>
    </div>
  `;
}
